import { pickActiveStep } from '@/components/deal-stepper/pickActiveStep';
import { packageInTransit } from '@/lib/socialProgress';
import { isSettled } from '@/stores/deals.store';
import type { HeaderUploadPhase } from '@/stores/voltrUploadPhase';
import { SERVER_STEP_STATE, type Action, type Deal, type DealStep } from '@/types/deal';

/**
 * The step the hero's button mirrors: the user's next action, only while it
 * is something to tap (a waiting or reported step has no button).
 */
export function heroCtaStep(deal: Deal, action: Action | undefined): DealStep | undefined {
  const step = pickActiveStep(deal.steps_config.steps, action);
  if (!step || step.terminal) return undefined;
  if (!action) return step;
  return action.step_states?.[step.key]?.state === SERVER_STEP_STATE.Active ? step : undefined;
}

/** The server's "fix this video" on any step, in step order (it rides on the upload step). */
export function heroAttention(deal: Deal, action: Action | undefined) {
  const states = action?.step_states ?? {};
  return deal.steps_config.steps.map((s) => states[s.key]?.attention).find((a) => !!a);
}

/**
 * The header's asks beyond the next step (Figma Create Deal Detail 5, 8, 9):
 * "Uploading content" while picks send; "Submit" while picks wait, going
 * where "Upload content" does (the uploader, its video rights above its own
 * Submit) and only while that uploader is on the page; "Track package"
 * while the package is on its way (nothing to film until it lands). None
 * once the deal is over (with or without the server's card) or the athlete
 * was removed from the program.
 */
export function heroUploadAsks(deal: Deal, action: Action | undefined, upload: HeaderUploadPhase) {
  const over = isSettled(action) || !!action?.voltr?.removed;
  const uploadStep = deal.steps_config.steps.find((s) => s.ctas?.some((c) => c.action === 'voltr_upload'));
  return {
    uploadStep,
    uploadCta: uploadStep?.ctas?.find((c) => c.action === 'voltr_upload'),
    uploading: !over && upload === 'uploading',
    submit: !over && upload === 'ready' && !!uploadStep && uploaderShown(uploadStep, action),
    trackable: !over && !upload && packageInTransit(action),
  };
}

/** The upload step draws its uploader (SocialStepRow): while it's the athlete's to do, or once it offers "Add another video". */
export function uploaderShown(step: DealStep, action: Action | undefined): boolean {
  const entry = action?.step_states?.[step.key];
  return entry?.state === SERVER_STEP_STATE.Active || !!entry?.add_label;
}

const WAITING_STATES: readonly string[] = [SERVER_STEP_STATE.Waiting, SERVER_STEP_STATE.Reported, SERVER_STEP_STATE.Overdue];

/** The server's label for the step that is waiting on someone else (it only ever sends one). */
export function heroWaitingLabel(deal: Deal, action: Action | undefined): string | undefined {
  const states = action?.step_states ?? {};
  const waiting = deal.steps_config.steps.find((s) => !s.terminal && WAITING_STATES.includes(states[s.key]?.state ?? ''));
  return waiting ? states[waiting.key]?.label : undefined;
}
